import { Department } from '../types/Company';

export type DepartmentTreeNode = {
  department: Department;
  children: DepartmentTreeNode[];
};

const sortNodes = (nodes: DepartmentTreeNode[]) => {
  nodes.sort((left, right) => left.department.name.localeCompare(right.department.name, undefined, { sensitivity: 'base' }));
  nodes.forEach(node => sortNodes(node.children));
};

export const buildDepartmentTree = (departments: Department[]): DepartmentTreeNode[] => {
  const byId = new Map(departments.map(department => [department.id, department]));
  const childrenByParent = new Map<string, Department[]>();

  departments.forEach(department => {
    const parentId = department.parentDepartmentId;
    if (!parentId || parentId === department.id || !byId.has(parentId)) {
      return;
    }

    const siblings = childrenByParent.get(parentId) || [];
    siblings.push(department);
    childrenByParent.set(parentId, siblings);
  });

  const buildNode = (department: Department, ancestors = new Set<string>()): DepartmentTreeNode => {
    if (ancestors.has(department.id)) {
      return { department, children: [] };
    }

    const nextAncestors = new Set(ancestors);
    nextAncestors.add(department.id);

    const children = (childrenByParent.get(department.id) || [])
      .map(child => buildNode(child, nextAncestors));

    sortNodes(children);
    return { department, children };
  };

  const roots = departments
    .filter(department => {
      const parentId = department.parentDepartmentId;
      return !parentId || parentId === department.id || !byId.has(parentId);
    })
    .map(department => buildNode(department));

  sortNodes(roots);
  return roots;
};
